/**
 * Unified brand token application — merges deck-tier + app-tier into one function.
 *
 * CSS Variable Ownership Manifest:
 * - Deck scope: --brand-* palette tokens, --brand-hue-rotate,
 *   --primary-dark*, --slide-*, and --ds-*.
 * - Full scope only: --brand-logo-filter, --destructive*,
 *   brand-coupled sidebar/action tokens, share action tokens, homepage
 *   brand chrome, onboarding brand chrome, and optional explicit app chart-palette tokens.
 * - Foundation tokens stay in globals.css: sidebar surfaces, chart semantic
 *   supporting colors, non-brand home colors,
 *   and foundation-only onboarding tokens (--onboarding-study-card-bg,
 *   --onboarding-study-card-description, --onboarding-preview-bg).
 * - `--ds-font-display` / `--ds-font-sans` bridge to `--font-display` / `--font-geist-sans` (set by :root[data-heading-font] / :root[data-font] selectors in globals.css), enabling the shell's active font to cascade into all slide components automatically.
 * - The deck's look (text, accent, panels, rules, chart slots) is the deck
 *   design system on the brand primary (`deriveDeckTheme`), the same function
 *   that writes the default theme into tokens.css.
 *
 * This helper is pure: input → DOM writes only. No React, no API, no auth.
 * Caller gates white-label and chooses token scope.
 */

import type { BrandSettings, BrandRoleGraph, BrandThemeSeedKey } from '@digital-twins/shared-types'
import { isWarmEmphasisFill } from '../chart-colors'
import { getChartPalette } from './chart-palettes'
import {
  DECK_THEME_TOKENS,
  FAIRGEN_PRIMARY_HSL,
  FAIRGEN_SOFT_ACCENT_DARK_HSL,
  FAIRGEN_SOFT_ACCENT_HSL,
  deckThemeCssVars,
  deriveDeckTheme,
} from './deck-design-system'

const DEFAULT_PRIMARY_COLOR = FAIRGEN_PRIMARY_HSL
const DEFAULT_SECONDARY_COLOR = FAIRGEN_SOFT_ACCENT_HSL
const DEFAULT_SECONDARY_COLOR_DARK = FAIRGEN_SOFT_ACCENT_DARK_HSL
const FIXED_DEEP_COLOR = '0 0% 0%'
const RED_SAFE_SURFACE_SOFT = '0 0% 96%'
const RED_SAFE_SURFACE_STRONG = '0 0% 92%'
const RED_SAFE_SURFACE_BORDER = '0 0% 84%'
const RED_SAFE_EMPHASIS = '0 0% 0%'
/**
 * Deck-ui-owned fallback for the diverging positive pole when the brand chart
 * color is diverging-unsafe (see `isDivergingUnsafeAccent`). `--chart-positive`
 * / `--chart-negative` were considered (per the plan's Step 1 outcome) but are
 * app-level foundation tokens: `apps/frontend/src/globals.css` defines them,
 * but `apps/backoffice/web/src/globals.css` does not, and deck-ui slides render
 * directly in backoffice (SlidePreviewFrame, SlideGalleryPage) without that
 * app's globals.css providing a value. `applyThemeBrandTokens` also never
 * writes them (they stay in `THEME_BRAND_FOUNDATION_TOKEN_KEYS`), so relying on
 * them here would silently resolve to an unstyled color in that host. Same
 * saturation/lightness as the hardcoded diverging negative pair
 * (`0 72% 42%` / `0 72% 94%`), opposite hue, so the two poles read as a
 * symmetric diverging scale.
 */
const DIVERGING_SAFE_POSITIVE = '158 72% 42%'
const DIVERGING_SAFE_POSITIVE_SOFT = '158 72% 94%'

/** The role-graph parts the brand apply still reads; text, accent and chart roles come from the deck design system. */
type RoleTokens = {
  brand: Omit<BrandRoleGraph['tokens']['brand'], 'textSubtle'>
  semantic: BrandRoleGraph['tokens']['semantic']
}
export type ThemeBrandTokenScope = 'full' | 'deck'
export type ThemeBrandTokenDisposer = () => void

export interface ApplyThemeBrandTokensOptions {
  /**
   * `full` preserves the historical behavior and writes every brand-owned token.
   * `deck` writes only tokens used by deck/slide rendering so product chrome stays Fairgen.
   */
  scope?: ThemeBrandTokenScope
  /**
   * When true, writes are tracked in a small per-token stack and the returned
   * disposer restores the previous values. Use for temporary surfaces like decks
   * and previews. Persistent app-level callers can keep the default direct write.
   */
  restore?: boolean
}

function clamp(num: number, min: number, max: number) {
  return Math.min(max, Math.max(min, num))
}

function normalizeHue(h: number) {
  const normalized = h % 360
  return normalized < 0 ? normalized + 360 : normalized
}

function parseHsl(hslString: string): { h: number; s: number; l: number } | null {
  const match = hslString.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/)
  if (!match) return null
  return {
    h: normalizeHue(parseFloat(match[1])),
    s: clamp(parseFloat(match[2]), 0, 100),
    l: clamp(parseFloat(match[3]), 0, 100),
  }
}

function isRedAccentTuple(hslString: string): boolean {
  const parsed = parseHsl(hslString)
  if (!parsed || parsed.s < 40) return false
  return parsed.h >= 345 || parsed.h < 15
}

/**
 * Widened, diverging-block-only variant of `isRedAccentTuple`. The diverging
 * negative pole is a hardcoded red (`hsl(0 72% 42%)`), so any color placed at
 * the positive pole needs enough hue distance from 0 to read as the opposite
 * end of the scale, not a second shade of the same warning color. A brown or
 * orange brand chart color (hue ~24) sits outside `isRedAccentTuple`'s +/-15deg
 * window but is still only 20-25deg from the hardcoded red: close enough to
 * collide. This predicate is intentionally NOT reused as `isRedAccentTuple`
 * because that shared predicate also drives `--slide-accent` to black and
 * brand card surfaces to neutral at every one of its call sites; widening it
 * would silently flip those for every orange brand product-wide. Used only by
 * the `--slide-diverging-*` block below.
 */
function isDivergingUnsafeAccent(hslString: string): boolean {
  const parsed = parseHsl(hslString)
  if (!parsed || parsed.s < 40) return false
  return parsed.h >= 340 || parsed.h < 40
}

function hexToHslTuple(hex: string): { h: number; s: number; l: number } | null {
  const cleaned = hex.replace('#', '')
  if (cleaned.length !== 3 && cleaned.length !== 6) return null
  const full = cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned
  const r = parseInt(full.slice(0, 2), 16) / 255
  const g = parseInt(full.slice(2, 4), 16) / 255
  const b = parseInt(full.slice(4, 6), 16) / 255
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l: l * 100 }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6
  return { h: h * 360, s: s * 100, l: l * 100 }
}

function formatHslTuple(h: number, s: number, l: number) {
  return `${Math.round(normalizeHue(h))} ${Math.round(clamp(s, 0, 100))}% ${Math.round(clamp(l, 0, 100))}%`
}

function tupleToCss(tuple: string) {
  return `hsl(${tuple})`
}

function normalizeHslTuple(value: string | null | undefined, fallback: string): string {
  const parsed = value ? parseHsl(value) : null
  if (!parsed) return fallback
  return formatHslTuple(parsed.h, parsed.s, parsed.l)
}

function normalizeColorValue(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  if (trimmed.startsWith('#')) {
    const hsl = hexToHslTuple(trimmed)
    return hsl ? formatHslTuple(hsl.h, hsl.s, hsl.l) : null
  }
  if (/^hsl\(/i.test(trimmed)) {
    const parsed = parseHsl(trimmed)
    return parsed ? formatHslTuple(parsed.h, parsed.s, parsed.l) : null
  }

  const parsed = parseHsl(trimmed)
  return parsed ? formatHslTuple(parsed.h, parsed.s, parsed.l) : null
}

function getThemeSeedValue(
  settings: BrandSettings,
  seedKey: BrandThemeSeedKey,
  resolvedTheme: 'light' | 'dark',
): string | null {
  const seed = settings.brandThemeConfig?.version === 1
    ? settings.brandThemeConfig.seeds?.[seedKey]
    : null
  const value = resolvedTheme === 'dark' ? seed?.dark : seed?.light
  return typeof value === 'string' ? normalizeColorValue(value) : null
}

function srgbToLinear(channel: number): number {
  const normalized = channel / 255
  if (normalized <= 0.04045) return normalized / 12.92
  return ((normalized + 0.055) / 1.055) ** 2.4
}

function getRelativeLuminance(rgb: { r: number; g: number; b: number }) {
  const r = srgbToLinear(rgb.r)
  const g = srgbToLinear(rgb.g)
  const b = srgbToLinear(rgb.b)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function getCoverAccentAlpha(rgb: { r: number; g: number; b: number }) {
  const luminance = getRelativeLuminance(rgb)
  if (luminance >= 0.65) return 0.24
  if (luminance >= 0.45) return 0.27
  if (luminance >= 0.25) return 0.31
  return 0.35
}

function getCoverBrandFieldAlpha(rgb: { r: number; g: number; b: number }) {
  const luminance = getRelativeLuminance(rgb)
  if (luminance >= 0.65) return 0.42
  if (luminance >= 0.45) return 0.48
  if (luminance >= 0.25) return 0.54
  return 0.58
}

function getCoverGraphiteTuples(accent: { h: number; s: number; l: number }) {
  if (accent.s < 12) {
    // Desaturated/grey/black brands: widen luminosity spread so the vertical gradient
    // has visible depth instead of rendering as an imperceptible near-black wash.
    return {
      base: '0 0% 10%',
      deep: '0 0% 6%',
      soft: '0 0% 22%',
    }
  }

  const graphiteSaturation = clamp(accent.s * 0.72, 32, 74)
  return {
    base: formatHslTuple(accent.h, graphiteSaturation, 12),
    deep: formatHslTuple(accent.h, graphiteSaturation, 8),
    soft: formatHslTuple(accent.h, graphiteSaturation, 24),
  }
}

function getCoverAccentTuple(accent: { h: number; s: number; l: number }) {
  if (accent.s < 12) return formatHslTuple(accent.h, 0, 72)
  return formatHslTuple(
    accent.h,
    clamp(accent.s + 10, 68, 100),
    clamp(accent.l + 16, 50, 62),
  )
}

function getCoverBrandFieldTuple(accent: { h: number; s: number; l: number }) {
  if (accent.s < 12) return formatHslTuple(accent.h, 0, 24)
  return formatHslTuple(
    accent.h,
    clamp(accent.s * 0.9, 54, 92),
    clamp(accent.l * 0.52, 22, 30),
  )
}

function getContrastRatio(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }) {
  const aLum = getRelativeLuminance(a)
  const bLum = getRelativeLuminance(b)
  const lighter = Math.max(aLum, bLum)
  const darker = Math.min(aLum, bLum)
  return (lighter + 0.05) / (darker + 0.05)
}

function hslToRgb(hslString: string): { r: number; g: number; b: number } {
  const parsed = parseHsl(hslString)
  // Fallback: Fairgen accent teal (#0EBFA5) as RGB
  if (!parsed) return { r: 14, g: 191, b: 165 }

  const h = parsed.h / 360
  const s = parsed.s / 100
  const l = parsed.l / 100

  let r: number
  let g: number
  let b: number

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      let tt = t
      if (tt < 0) tt += 1
      if (tt > 1) tt -= 1
      if (tt < 1 / 6) return p + (q - p) * 6 * tt
      if (tt < 1 / 2) return q
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  }
}

function getPrimaryForeground(hslString: string): string {
  const colorRgb = hslToRgb(hslString)
  const white = { r: 255, g: 255, b: 255 }
  const black = { r: 0, g: 0, b: 0 }

  const whiteContrast = getContrastRatio(colorRgb, white)
  const blackContrast = getContrastRatio(colorRgb, black)

  return whiteContrast >= blackContrast ? '0 0% 100%' : '0 0% 0%'
}

function getActionForeground(hslString: string): string {
  const { r, g, b } = hslToRgb(hslString)
  const luminance = 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b)
  // Threshold 0.24 (vs getPrimaryForeground's contrast-ratio approach) biases toward
  // white text on saturated/dark brand colors for better action button readability.
  return luminance <= 0.24 ? '0 0% 100%' : '0 0% 0%'
}

const READABLE_ON_BRAND_SURFACE_MIN_CONTRAST = 4.5

/**
 * Foreground for labels/icons on primary-, CTA-, and send-filled surfaces.
 *
 * Uses luminance-based preference rather than a light/dark-mode bias:
 * - Luminance < 0.20 → color reads as "dark" → prefer white text.
 * - Luminance ≥ 0.20 → color reads as "light" → prefer black text.
 *
 * This correctly handles the borderline band (luminance 0.17–0.22) where both
 * black and white technically pass 4.5:1 but one is perceptually much clearer
 * (e.g. white on a medium-dark purple at L≈0.18 is far more readable than black,
 * even though black scores fractionally higher in the WCAG ratio).
 *
 * Only used by applyThemeBrandTokens (white-label full-app branding).
 */
function getReadableOnBrandSurfaceForeground(
  hslString: string,
  resolvedTheme: 'light' | 'dark',
): string {
  const parsed = parseHsl(hslString)
  if (parsed && isWarmEmphasisFill(parsed.h, parsed.s, parsed.l)) {
    return '0 0% 100%'
  }

  const bg = hslToRgb(hslString)
  const white = { r: 255, g: 255, b: 255 }
  const black = { r: 0, g: 0, b: 0 }
  const onWhite = getContrastRatio(bg, white)
  const onBlack = getContrastRatio(bg, black)
  const bgLuminance = getRelativeLuminance(bg)

  // Prefer white text on perceptually-dark colors, black text on perceptually-light ones.
  const preferWhite = bgLuminance < 0.20

  if (preferWhite) {
    if (onWhite >= READABLE_ON_BRAND_SURFACE_MIN_CONTRAST) return '0 0% 100%'
    if (onBlack >= READABLE_ON_BRAND_SURFACE_MIN_CONTRAST) return '0 0% 0%'
  } else {
    if (onBlack >= READABLE_ON_BRAND_SURFACE_MIN_CONTRAST) return '0 0% 0%'
    if (onWhite >= READABLE_ON_BRAND_SURFACE_MIN_CONTRAST) return '0 0% 100%'
  }
  // Neither passes 4.5:1 (extremely narrow mid-luminance band): use higher-contrast
  // option with a dark-mode fallback to white for all-dark UI consistency.
  return onWhite > onBlack
    ? '0 0% 100%'
    : resolvedTheme === 'dark' ? '0 0% 100%' : '0 0% 0%'
}

/**
 * Brand-colored text on a surface (links, text-primary on cards).
 * Dark mode falls back to white; light mode falls back to black when contrast fails.
 * Only written by applyThemeBrandTokens for white-label full-app branding.
 */
function getReadableBrandTextOnSurface(
  textColorTuple: string,
  surfaceBackgroundTuple: string,
  resolvedTheme: 'light' | 'dark',
): string {
  if (
    getContrastRatio(hslToRgb(textColorTuple), hslToRgb(surfaceBackgroundTuple)) >=
    READABLE_ON_BRAND_SURFACE_MIN_CONTRAST
  ) {
    return textColorTuple
  }
  return resolvedTheme === 'dark' ? '0 0% 100%' : '0 0% 0%'
}

const STEP_BADGE_MIN_CONTRAST = 3

/**
 * Stepper active circle must separate from the page background (WCAG 3:1 for UI).
 * Tries CTA then accent; lightens accent if both are too dark on the surface.
 */
function resolveStepActiveBgTuple(
  accentTuple: string,
  ctaTuple: string,
  surfaceTuple: string,
): string {
  const candidates = [ctaTuple, accentTuple]
  for (const candidate of candidates) {
    if (
      getContrastRatio(hslToRgb(candidate), hslToRgb(surfaceTuple)) >= STEP_BADGE_MIN_CONTRAST
    ) {
      return candidate
    }
  }
  const accent = parseHsl(accentTuple) || { h: 171, s: 86, l: 40 }
  let lightness = accent.l
  while (lightness <= 85) {
    const tuple = formatHslTuple(accent.h, accent.s, lightness)
    if (getContrastRatio(hslToRgb(tuple), hslToRgb(surfaceTuple)) >= STEP_BADGE_MIN_CONTRAST) {
      return tuple
    }
    lightness += 6
  }
  return formatHslTuple(accent.h, clamp(accent.s, 34, 86), 72)
}

function resolveWizardStepTokens(
  accentTuple: string,
  ctaTuple: string,
  actionFg: string,
  cardTuple: string,
  cardForegroundTuple: string,
  pageBackgroundTuple: string,
  resolvedTheme: 'light' | 'dark',
): Record<string, string> {
  if (resolvedTheme === 'light') {
    return {
      '--step-active-bg': accentTuple,
      '--step-active-fg': actionFg,
      '--step-completed-bg': accentTuple,
      '--step-completed-fg': actionFg,
      '--step-active-ring': accentTuple,
      '--step-active-label': cardForegroundTuple,
      '--step-completed-label': '0 0% 42%', // AA-corrected: was 45%, now 5.32:1 on white bg
      '--step-pending-bg': '0 0% 94.5%',
      '--step-pending-fg': '0 0% 42%',      // AA-corrected: was 45% (4.21:1 on 94.5% bg) → 4.71:1
      '--step-pending-label': '0 0% 42%',   // AA-corrected: was 61% (2.76:1) → 5.32:1
      '--step-connector': '0 0% 88.6%',
    }
  }

  const activeBg = resolveStepActiveBgTuple(accentTuple, ctaTuple, pageBackgroundTuple)
  const activeFg = getReadableOnBrandSurfaceForeground(activeBg, 'dark')
  const completedFg = getReadableOnBrandSurfaceForeground(accentTuple, 'dark')

  return {
    '--step-active-bg': activeBg,
    '--step-active-fg': activeFg,
    '--step-completed-bg': accentTuple,
    '--step-completed-fg': completedFg,
    '--step-active-ring': activeBg,
    '--step-active-label': '0 0% 100%',
    '--step-completed-label': '0 0% 74%',
    '--step-pending-bg': cardTuple,
    '--step-pending-fg': cardForegroundTuple,
    '--step-pending-label': '0 0% 74%',
    '--step-connector': '171 26% 15%',
  }
}

function resolveThemeSurfaceTuple(
  settings: BrandSettings,
  token: '--background' | '--card',
  resolvedTheme: 'light' | 'dark',
  fallback: string,
): string {
  const override = settings.themeOverrides?.[token]
  if (override) {
    const parsed = parseHsl(override.replace(/\/.*$/, '').trim())
    if (parsed) {
      return formatHslTuple(parsed.h, parsed.s, parsed.l)
    }
  }
  return fallback
}

function buildFallbackRoleTokens(accentTuple: string, cardTuple: string): RoleTokens {
  const accent = parseHsl(accentTuple) || { h: 171, s: 86, l: 40 }
  const card = parseHsl(cardTuple) || { h: 154, s: 85, l: 84 }

  const safeAccent = {
    h: accent.h,
    s: clamp(accent.s, 34, 86),
    l: clamp(accent.l, 24, 40),
  }
  const safeCard = { ...card }

  return {
    brand: {
      accent: formatHslTuple(safeAccent.h, safeAccent.s, safeAccent.l),
      accentForeground: getPrimaryForeground(formatHslTuple(safeAccent.h, safeAccent.s, safeAccent.l)),
      surfaceSoft: formatHslTuple(safeAccent.h, clamp(safeAccent.s - 24, 10, 62), 95),
      surfaceStrong: formatHslTuple(safeAccent.h, clamp(safeAccent.s - 12, 20, 74), clamp(safeAccent.l + 4, 28, 44)),
      border: formatHslTuple(safeAccent.h, clamp(safeAccent.s - 18, 14, 66), clamp(safeAccent.l + 22, 40, 62)),
      card: formatHslTuple(safeCard.h, safeCard.s, safeCard.l),
      cardForeground: getPrimaryForeground(formatHslTuple(safeCard.h, safeCard.s, safeCard.l)),
      deep: FIXED_DEEP_COLOR,
      deepForeground: '0 0% 100%',
    },
    semantic: {
      success: '143 72% 30%',
      successSoft: '143 72% 92%',
      warning: '23 84% 36%',
      warningSoft: '23 84% 92%',
      danger: '0 72% 42%',
      dangerSoft: '0 72% 94%',
      info: '215 74% 42%',
      infoSoft: '215 74% 93%',
    },
  }
}

function resolveDarkCardTuple(cardTuple: string): string {
  const parsed = parseHsl(cardTuple)
  if (!parsed) return DEFAULT_SECONDARY_COLOR_DARK
  return formatHslTuple(parsed.h, clamp(parsed.s - 32, 18, 54), 12)
}

function resolveSeedTuples(settings: BrandSettings, resolvedTheme: 'light' | 'dark') {
  const accentTuple = normalizeHslTuple(
    getThemeSeedValue(settings, 'accent', resolvedTheme) ||
      normalizeColorValue(settings.brandPrimaryColor) ||
      DEFAULT_PRIMARY_COLOR,
    DEFAULT_PRIMARY_COLOR,
  )
  const lightCardTuple = normalizeHslTuple(
    normalizeColorValue(settings.brandSecondaryColor) || DEFAULT_SECONDARY_COLOR,
    DEFAULT_SECONDARY_COLOR,
  )
  const cardTuple = normalizeHslTuple(
    getThemeSeedValue(settings, 'card', resolvedTheme) ||
      (resolvedTheme === 'dark' ? resolveDarkCardTuple(lightCardTuple) : lightCardTuple),
    resolvedTheme === 'dark' ? DEFAULT_SECONDARY_COLOR_DARK : DEFAULT_SECONDARY_COLOR,
  )
  return { accentTuple, cardTuple }
}

/**
 * The org's chosen app chart palette (primary first), or null when none is
 * chosen. App charts only: deck chart slots come from the deck design system,
 * because these one-hue ramps fail the categorical colour-blind checks.
 */
function resolveAppChartTuples(settings: BrandSettings): Array<string> | null {
  const chartPaletteId = settings.brandThemeConfig?.version === 1
    ? settings.brandThemeConfig.chartPaletteId
    : null
  if (!chartPaletteId) return null
  const palette = getChartPalette(chartPaletteId)
  const primary = palette.colors[palette.primaryIndex] ?? palette.colors[0]
  return [primary, ...palette.colors.filter((_, index) => index !== palette.primaryIndex)]
}

function resolveRoleTokens(settings: BrandSettings, resolvedTheme: 'light' | 'dark'): RoleTokens {
  const { accentTuple, cardTuple } = resolveSeedTuples(settings, resolvedTheme)
  const fallback = buildFallbackRoleTokens(accentTuple, cardTuple)
  const graph = settings.brandRoleGraph?.tokens

  if (!graph) return fallback

  return {
    brand: {
      accent: accentTuple,
      accentForeground: getPrimaryForeground(accentTuple),
      surfaceSoft: normalizeHslTuple(graph.brand?.surfaceSoft, fallback.brand.surfaceSoft),
      surfaceStrong: normalizeHslTuple(graph.brand?.surfaceStrong, fallback.brand.surfaceStrong),
      border: normalizeHslTuple(graph.brand?.border, fallback.brand.border),
      card: cardTuple,
      cardForeground: getPrimaryForeground(cardTuple),
      deep: normalizeHslTuple(graph.brand?.deep, fallback.brand.deep),
      deepForeground: normalizeHslTuple(graph.brand?.deepForeground, fallback.brand.deepForeground),
    },
    semantic: {
      success: normalizeHslTuple(graph.semantic?.success, fallback.semantic.success),
      successSoft: normalizeHslTuple(graph.semantic?.successSoft, fallback.semantic.successSoft),
      warning: normalizeHslTuple(graph.semantic?.warning, fallback.semantic.warning),
      warningSoft: normalizeHslTuple(graph.semantic?.warningSoft, fallback.semantic.warningSoft),
      danger: normalizeHslTuple(graph.semantic?.danger, fallback.semantic.danger),
      dangerSoft: normalizeHslTuple(graph.semantic?.dangerSoft, fallback.semantic.dangerSoft),
      info: normalizeHslTuple(graph.semantic?.info, fallback.semantic.info),
      infoSoft: normalizeHslTuple(graph.semantic?.infoSoft, fallback.semantic.infoSoft),
    },
  }
}

/**
 * Resolve the deep seed color with priority:
 * 1. settings.themeOverrides?.['--deep']
 * 2. settings.brandRoleGraph?.seed?.deep
 * 3. Derived from accent hue at L=8% S=52%
 */
/**
 * User message bubbles and the chat send button. Derived from brand accent so
 * orgs with only a primary color (no secondary) still get on-brand chat chrome.
 */
function resolveTwinChatSendTuple(
  accentTuple: string,
  cardTuple: string,
  resolvedTheme: 'light' | 'dark',
): { send: string; sendFg: string } {
  const accent = parseHsl(accentTuple) || { h: 171, s: 86, l: 40 }
  const card = parseHsl(cardTuple)

  if (resolvedTheme === 'dark') {
    if (card && card.l >= 65) {
      return {
        send: cardTuple,
        sendFg: getReadableOnBrandSurfaceForeground(cardTuple, resolvedTheme),
      }
    }
    const send = formatHslTuple(
      accent.h,
      clamp(accent.s * 0.85, 28, 90),
      clamp(accent.l + 44, 76, 86),
    )
    return { send, sendFg: getReadableOnBrandSurfaceForeground(send, resolvedTheme) }
  }

  const send = formatHslTuple(
    accent.h,
    clamp(accent.s * 0.7, 20, 80),
    clamp(accent.l + 46, 84, 93),
  )
  return { send, sendFg: getReadableOnBrandSurfaceForeground(send, resolvedTheme) }
}

/** Assistant bubble base color (opacity applied in Tailwind). */
function resolveTwinChatBubbleTuple(
  roleTokens: RoleTokens,
  resolvedTheme: 'light' | 'dark',
): string {
  return resolvedTheme === 'dark'
    ? roleTokens.brand.surfaceStrong
    : roleTokens.brand.surfaceSoft
}

function resolveDeepSeed(
  settings: BrandSettings,
  accentTuple: string,
  resolvedTheme: 'light' | 'dark',
): string {
  const structuredDeep = getThemeSeedValue(settings, 'deep', resolvedTheme)
  if (structuredDeep) return structuredDeep

  const overrideDeep = settings.themeOverrides?.['--deep']
  if (overrideDeep) {
    const parsed = parseHsl(overrideDeep)
    if (parsed) return formatHslTuple(parsed.h, parsed.s, parsed.l)
  }

  const graphDeep = settings.brandRoleGraph?.seed?.deep
  if (graphDeep) {
    const parsed = parseHsl(graphDeep)
    if (parsed) return formatHslTuple(parsed.h, parsed.s, parsed.l)
  }

  const accent = parseHsl(accentTuple)
  if (accent) return formatHslTuple(accent.h, 52, 8)
  return formatHslTuple(171, 52, 8)
}

/**
 * Deck-scope keys are safe to write for generated deck rendering even when the
 * surrounding product shell should stay Fairgen-branded.
 */
export const THEME_BRAND_DECK_TOKEN_KEYS: Array<string> = [
  // Brand tokens
  '--brand-accent',
  '--brand-accent-foreground',
  '--brand-card',
  '--brand-card-foreground',
  '--brand-card-rgb',
  '--brand-deep',
  '--brand-deep-foreground',
  '--brand-deep-rgb',
  '--brand-hue-rotate',
  // Primary-dark
  '--primary-dark',
  '--primary-dark-foreground',
  '--primary-dark-rgb',
  // Slide text
  '--slide-text-primary',
  '--slide-text-secondary',
  '--slide-text-muted',
  // Slide accent
  '--slide-accent',
  '--slide-accent-foreground',
  '--slide-accent-alt',
  '--slide-accent-alt-foreground',
  '--slide-accent-soft',
  '--slide-highlight',
  '--slide-highlight-soft',
  // Slide semantic
  '--slide-success',
  '--slide-success-soft',
  '--slide-warning',
  '--slide-warning-soft',
  '--slide-danger',
  '--slide-danger-soft',
  '--slide-info',
  '--slide-info-soft',
  // Slide diverging
  '--slide-diverging-positive',
  '--slide-diverging-positive-soft',
  '--slide-diverging-negative',
  '--slide-diverging-negative-soft',
  '--slide-diverging-neutral',
  // Slide caution
  '--slide-caution-bg',
  '--slide-caution-text',
  '--slide-caution-text-strong',
  // Slide cover
  '--slide-cover-bg',
  '--slide-cover-text',
  '--slide-cover-text-muted',
  // Slide brand card/deep
  '--slide-brand-card-accent',
  '--slide-brand-card-bg',
  '--slide-brand-card-border',
  '--slide-brand-card-foreground',
  '--slide-brand-deep-bg',
  '--slide-brand-deep-foreground',
  // Slide heatmap
  '--slide-heatmap-green-start',
  '--slide-heatmap-green-end',
  '--slide-heatmap-green-star',
  '--slide-heatmap-red-start',
  '--slide-heatmap-red-end',
  '--slide-heatmap-red-star',
  '--slide-heatmap-red-text',
  '--slide-heatmap-green-text',
  // Slide neutral and surfaces (deck design system)
  '--slide-neutral-medium',
  DECK_THEME_TOKENS.panel,
  DECK_THEME_TOKENS.rule,
  DECK_THEME_TOKENS.neutralLight,
  // Slide chart
  '--slide-chart-1',
  '--slide-chart-2',
  '--slide-chart-3',
  '--slide-chart-4',
  '--slide-chart-5',
  '--slide-chart-6',
  '--slide-chart-7',
  '--slide-chart-8',
  '--slide-chart-9',
  '--slide-chart-10',
  '--slide-chart-11',
  // DS bridge tokens
  '--ds-accent',
  '--ds-accent-alt',
  '--ds-chart-accent',
  '--ds-chart-accent-alt',
  '--ds-accent-secondary',
  '--ds-quadrant-badge-border',
  '--ds-red-tint',
  '--ds-red-tint-light',
  '--ds-danger-soft',
  '--ds-heatmap-high-bg',
  '--ds-heatmap-high-text',
  '--ds-heatmap-mid-bg',
  '--ds-heatmap-mid-text',
  '--ds-cover-accent',
  '--ds-cover-accent-rgb',
  '--ds-cover-brand-field-rgb',
  '--ds-cover-accent-alpha',
  '--ds-cover-brand-field-alpha',
  '--ds-cover-pattern-opacity',
  '--ds-cover-graphite-bg',
  '--ds-cover-graphite-bg-deep',
  '--ds-cover-graphite-bg-soft',
  '--ds-cover-meta-label-accent',
]

/**
 * Full-scope-only keys affect product/app chrome and must only be written for
 * white-label-enabled experiences.
 */
export const THEME_BRAND_FULL_SCOPE_ONLY_TOKEN_KEYS: Array<string> = [
  // App logo/chrome helpers
  '--brand-logo-filter',
  // Destructive app semantics
  '--destructive',
  '--destructive-foreground',
  // Sidebar brand-coupled tokens. Sidebar surfaces/foreground/borders stay in globals.css.
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-ring',
  // Optional app chart series palette. Values are written only when chartPaletteId is explicit.
  // Semantic chart status tokens such as --chart-negative stay in globals.css.
  '--chart-primary',
  '--chart-secondary',
  '--chart-tertiary',
  '--chart-accent',
  '--chart-quinary',
  '--chart-senary',
  '--chart-bg-dark',
  '--chart-bg-dark-rgb',
  // Brand action tokens
  '--primary',
  '--primary-foreground',
  '--ring',
  '--cta',
  '--cta-foreground',
  '--sidebar-cta-gradient',
  '--sidebar-cta-foreground',
  '--share-action',
  '--share-action-foreground',
  '--share-gate-icon',
  '--share-gate-icon-background',
  '--twin-chat-accent',
  '--twin-chat-send',
  '--twin-chat-send-fg',
  '--twin-chat-bubble',
  '--primary-text-on-background',
  '--primary-text-on-card',
  '--report-action',
  '--report-accent-light',
  '--report-accent-mid',
  // Homepage brand-coupled carousel selection chrome.
  '--home-card-active-border',
  '--home-card-active-check-bg',
  '--home-card-active-check-fg',
  '--home-card-shadow',
  '--home-card-shadow-active',
  '--home-chip-bg',
  '--home-chip-bg-hover',
  '--home-chip-fg',
  // Report secondary affordances (utility icon chips — not primary CTAs).
  '--report-secondary-icon-bg',
  '--report-secondary-icon-fg',
  // Marketplace audience section badge (Store icon circle).
  // Defaults to Fairgen platform identity in globals.css; brand orgs get primary.
  '--audience-badge-bg',
  '--audience-badge-fg',
  // Identity-mix slider track (audience wizard age bands). Fairgen base is deep
  // green (green-on-green); brand orgs get a neutral graphite so the track never
  // reads as Fairgen green on a non-green brand.
  '--audience-slider-track',
  // Segment/attribute option chip background (dark only). Fairgen keeps the deep
  // teal --card-inset; brand orgs mirror the sidebar selected color so chips don't
  // read as Fairgen green on a non-green brand.
  '--segment-option-bg',
  // Homepage utility chip icon — Fairgen chrome paints this from --platform-*
  // foundation tokens (mint/deep-teal). White-label orgs neutralize the arrow
  // because brand accents on the chip bg often clash, so we force the muted
  // control color here.
  '--home-chip-icon',
  // Progress bar accent — red brands fall back to foreground so bars don't read as errors.
  '--progress-bar-accent',
  // Wizard stepper (study creation, test wizard) — readable brand colors on surfaces.
  '--step-active-bg',
  '--step-active-fg',
  '--step-completed-bg',
  '--step-completed-fg',
  '--step-active-ring',
  '--step-active-label',
  '--step-completed-label',
  '--step-pending-bg',
  '--step-pending-fg',
  '--step-pending-label',
  '--step-connector',
  // Interactive dashed-border / focus accent (questionnaire builder, filters).
  '--accent-highlight',
  // Plan "Most popular" badge accent — red brands fall back to foreground so the
  // pricing highlight is never red.
  '--plan-badge-accent',
  // Onboarding brand chrome — brand-overridable subset.
  // Foundation-only tokens (--onboarding-study-card-bg, --onboarding-study-card-description,
  // --onboarding-preview-bg) are intentionally omitted; they stay in globals.css for all orgs.
  '--onboarding-study-pill-bg',
  '--onboarding-study-pill-fg',
  '--onboarding-study-pill-border',
  '--onboarding-brand-hero-bg',
  '--onboarding-brand-hero-fg',
  '--onboarding-brand-hero-glow',
  '--onboarding-portrait-deep',
]

/**
 * Foundation-owned keys are intentionally not written by applyThemeBrandTokens.
 * They remain in globals.css so the Fairgen product foundation has exactly one
 * source of truth. Keep them in reset cleanup to remove stale inline values
 * created by older builds before this sparse-writer split.
 */
export const THEME_BRAND_FOUNDATION_TOKEN_KEYS: Array<string> = [
  '--sidebar-background',
  '--sidebar-foreground',
  '--sidebar-accent',
  '--sidebar-accent-foreground',
  '--sidebar-border',
  '--chart-positive',
  '--chart-negative',
  '--chart-warning',
  '--chart-neutral',
  '--chart-muted',
  '--chart-muted-dark',
  '--home-submit-fg',
  '--home-pill-active-fg',
  '--home-stepper-count-fg',
]

/**
 * All CSS custom property keys written by applyThemeBrandTokens.
 * Used by resetThemeBrandTokens to clear inline styles.
 */
const ALL_APPLY_TOKEN_KEYS: Array<string> = [
  ...THEME_BRAND_DECK_TOKEN_KEYS,
  ...THEME_BRAND_FULL_SCOPE_ONLY_TOKEN_KEYS,
]

const ALL_RESET_TOKEN_KEYS: Array<string> = [
  ...ALL_APPLY_TOKEN_KEYS,
  ...THEME_BRAND_FOUNDATION_TOKEN_KEYS,
]

export type ThemeBrandTokenValues = Record<string, string>

interface TokenStackEntry {
  id: number
  hadPreviousValue: boolean
  previousValue: string
}

const tokenStacks = new Map<string, Array<TokenStackEntry>>()
let tokenStackOwnerId = 0

function getThemeBrandTokenKeys(scope: ThemeBrandTokenScope): Array<string> {
  if (scope === 'deck') {
    return THEME_BRAND_DECK_TOKEN_KEYS
  }
  return ALL_APPLY_TOKEN_KEYS
}

function getThemeBrandResetTokenKeys(scope: ThemeBrandTokenScope): Array<string> {
  if (scope === 'deck') {
    return THEME_BRAND_DECK_TOKEN_KEYS
  }
  return ALL_RESET_TOKEN_KEYS
}

function restoreTokenValue(
  root: CSSStyleDeclaration,
  key: string,
  entry: Pick<TokenStackEntry, 'hadPreviousValue' | 'previousValue'>,
) {
  if (entry.hadPreviousValue) {
    root.setProperty(key, entry.previousValue)
  } else {
    root.removeProperty(key)
  }
}

function applyTokenValuesDirectly(
  root: CSSStyleDeclaration,
  tokenValues: ThemeBrandTokenValues,
  keys: Array<string>,
): ThemeBrandTokenDisposer {
  for (const key of keys) {
    const value = tokenValues[key]
    if (value !== undefined) {
      root.setProperty(key, value)
    }
  }
  return () => {}
}

function applyTokenValuesWithRestore(
  root: CSSStyleDeclaration,
  tokenValues: ThemeBrandTokenValues,
  keys: Array<string>,
): ThemeBrandTokenDisposer {
  const id = ++tokenStackOwnerId
  const ownedKeys: Array<string> = []

  for (const key of keys) {
    const value = tokenValues[key]
    if (value === undefined) {
      continue
    }

    const previousValue = root.getPropertyValue(key)
    const stack = tokenStacks.get(key) ?? []
    stack.push({
      id,
      hadPreviousValue: previousValue !== '',
      previousValue,
    })
    tokenStacks.set(key, stack)
    ownedKeys.push(key)
    root.setProperty(key, value)
  }

  let disposed = false
  return () => {
    if (disposed) {
      return
    }
    disposed = true

    for (const key of ownedKeys) {
      const stack = tokenStacks.get(key)
      if (!stack) {
        continue
      }

      const entryIndex = stack.findIndex((entry) => entry.id === id)
      if (entryIndex === -1) {
        continue
      }

      const [removedEntry] = stack.splice(entryIndex, 1)
      if (!removedEntry) {
        continue
      }
      const wasTopOwner = entryIndex === stack.length

      if (!wasTopOwner && stack[entryIndex]) {
        // The next owner had captured the removed owner's value as its previous
        // value. Preserve restore semantics by relinking it to the removed
        // owner's own predecessor.
        stack[entryIndex].hadPreviousValue = removedEntry.hadPreviousValue
        stack[entryIndex].previousValue = removedEntry.previousValue
      }

      if (stack.length === 0) {
        tokenStacks.delete(key)
      }

      if (wasTopOwner) {
        restoreTokenValue(root, key, removedEntry)
      }
    }
  }
}

function writeThemeBrandTokenValues(
  tokenValues: ThemeBrandTokenValues,
  options: ApplyThemeBrandTokensOptions,
): ThemeBrandTokenDisposer {
  const root = document.documentElement.style
  const scope = options.scope ?? 'full'
  const keys = getThemeBrandTokenKeys(scope)
  if (options.restore === true) {
    return applyTokenValuesWithRestore(root, tokenValues, keys)
  }
  return applyTokenValuesDirectly(root, tokenValues, keys)
}

/**
 * Apply brand tokens to document root CSS variables.
 *
 * Default `scope: 'full'` preserves the historical all-token behavior.
 * `scope: 'deck'` writes only generated-deck tokens so app/share chrome can
 * remain Fairgen-branded when white-label is disabled.
 */
export function applyThemeBrandTokens(
  settings: BrandSettings,
  resolvedTheme: 'light' | 'dark',
  options: ApplyThemeBrandTokensOptions = {},
): ThemeBrandTokenDisposer {
  return writeThemeBrandTokenValues(resolveThemeBrandTokenValues(settings, resolvedTheme), options)
}

/**
 * The deck-scope custom properties a brand's deck renders with: what
 * `applyThemeBrandTokens(…, { scope: 'deck' })` writes, as values. The
 * PowerPoint export sends them with the deck, so the file draws in the colours
 * the viewer shows without deriving them a second time.
 */
export function resolveDeckThemeTokenValues(
  settings: BrandSettings,
  resolvedTheme: 'light' | 'dark',
): ThemeBrandTokenValues {
  const values = resolveThemeBrandTokenValues(settings, resolvedTheme)
  return Object.fromEntries(THEME_BRAND_DECK_TOKEN_KEYS.filter((key) => key in values).map((key) => [key, values[key]]))
}

function resolveThemeBrandTokenValues(
  settings: BrandSettings,
  resolvedTheme: 'light' | 'dark',
): ThemeBrandTokenValues {
  const roleTokens = resolveRoleTokens(settings, resolvedTheme)

  const accentTuple = roleTokens.brand.accent
  const accentForegroundTuple = roleTokens.brand.accentForeground
  const cardTuple = roleTokens.brand.card
  const cardForegroundTuple = roleTokens.brand.cardForeground

  const accentParsed = parseHsl(accentTuple) || { h: 171, s: 86, l: 40 }

  // Deep seed with priority: themeOverrides → roleGraph.seed.deep → derived from accent
  const deepTuple = resolveDeepSeed(settings, accentTuple, resolvedTheme)
  const deepForegroundTuple = getPrimaryForeground(deepTuple)
  const ctaTuple = normalizeHslTuple(
    getThemeSeedValue(settings, 'cta', resolvedTheme) ||
      (resolvedTheme === 'dark' ? cardTuple : deepTuple),
    resolvedTheme === 'dark' ? cardTuple : deepTuple,
  )
  const destructiveTuple = normalizeHslTuple(
    getThemeSeedValue(settings, 'destructive', resolvedTheme) ||
      roleTokens.semantic.danger,
    roleTokens.semantic.danger,
  )

  const deepRgb = hslToRgb(deepTuple)
  const cardRgb = hslToRgb(cardTuple)
  const brandLogoFilter =
    deepForegroundTuple === '0 0% 100%' ? 'brightness(0) invert(1)' : 'none'

  const cardCss = tupleToCss(cardTuple)
  const deepCss = tupleToCss(deepTuple)

  const chartAccentTuple = normalizeHslTuple(
    getThemeSeedValue(settings, 'chartAccent', resolvedTheme) ||
      accentTuple,
    accentTuple,
  )
  // The deck's look: the deck design system on the brand primary (and chart
  // accent), the same derivation as the default theme in tokens.css.
  // Slot 1 follows the org's chosen chart palette, else its chart accent.
  const appChartTuples = resolveAppChartTuples(settings)
  const deckTheme = deriveDeckTheme({ primary: accentTuple, chartAccent: appChartTuples?.[0] ?? chartAccentTuple })
  const deckThemeVars = deckThemeCssVars(deckTheme)
  const chartPrimaryTuple = normalizeColorValue(deckTheme.chart[0]) as string
  const chartPrimaryCss = deckTheme.chart[0]
  const chartMidCss = deckTheme.chart[3]
  const chartPrimaryForegroundCss = tupleToCss(getPrimaryForeground(chartPrimaryTuple))
  const usesRedSafeSurfaces =
    isRedAccentTuple(accentTuple) ||
    isRedAccentTuple(roleTokens.brand.surfaceSoft) ||
    isRedAccentTuple(roleTokens.brand.surfaceStrong)
  const usesRedSafeEmphasis = isRedAccentTuple(accentTuple)
  const slideBrandEmphasisTuple = usesRedSafeEmphasis
    ? RED_SAFE_EMPHASIS
    : (normalizeColorValue(deckTheme.accent) as string)
  const slideBrandEmphasisCss = usesRedSafeEmphasis ? tupleToCss(RED_SAFE_EMPHASIS) : deckTheme.accent
  const slideBrandEmphasisForegroundTuple = getPrimaryForeground(slideBrandEmphasisTuple)
  const cardSurfaceUsesNeutralFallback =
    usesRedSafeSurfaces ||
    isRedAccentTuple(cardTuple)
  const slideAccentSoftCss = usesRedSafeSurfaces
    ? tupleToCss(RED_SAFE_SURFACE_SOFT)
    : deckTheme.accentSoft
  const highlightSoftTuple = usesRedSafeSurfaces
    ? RED_SAFE_SURFACE_STRONG
    : formatHslTuple(
        accentParsed.h,
        clamp(accentParsed.s - 20, 0, 100),
        clamp(accentParsed.l + 40, 0, 100),
      )
  const highlightSoftCss = tupleToCss(highlightSoftTuple)
  const neutralCardBgCss = `color-mix(in srgb, ${tupleToCss(RED_SAFE_SURFACE_STRONG)} 42%, var(--slide-card-bg))`
  const neutralCardBorderCss = `color-mix(in srgb, ${tupleToCss(RED_SAFE_SURFACE_BORDER)} 70%, var(--slide-border-soft))`
  const brandCardBgCss = cardSurfaceUsesNeutralFallback
    ? neutralCardBgCss
    : `color-mix(in srgb, ${cardCss} 14%, var(--slide-card-bg))`
  const brandCardBorderCss = cardSurfaceUsesNeutralFallback
    ? neutralCardBorderCss
    : `color-mix(in srgb, ${cardCss} 34%, var(--slide-border-soft))`

  const tokenValues: ThemeBrandTokenValues = {}
  const setToken = (key: string, value: string) => {
    tokenValues[key] = value
  }

  // ── Brand tokens ──
  setToken('--brand-accent', accentTuple)
  setToken('--brand-accent-foreground', accentForegroundTuple)
  setToken('--brand-card', cardTuple)
  setToken('--brand-card-foreground', cardForegroundTuple)
  setToken('--brand-card-rgb', `${cardRgb.r}, ${cardRgb.g}, ${cardRgb.b}`)
  setToken('--brand-deep', deepTuple)
  setToken('--brand-deep-foreground', deepForegroundTuple)
  setToken('--brand-deep-rgb', `${deepRgb.r}, ${deepRgb.g}, ${deepRgb.b}`)
  setToken('--brand-logo-filter', brandLogoFilter)

  // ── Primary-dark (deck dark backgrounds) ──
  setToken('--primary-dark', deepTuple)
  setToken('--primary-dark-foreground', deepForegroundTuple)
  setToken('--primary-dark-rgb', `${deepRgb.r}, ${deepRgb.g}, ${deepRgb.b}`)

  // ── Destructive semantic tokens ──
  setToken('--destructive', destructiveTuple)
  setToken('--destructive-foreground', getPrimaryForeground(destructiveTuple))

  // ── Optional app chart series palette tokens ──
  // Fairgen/default app charts are globals.css-owned. Only explicit org palette
  // selection writes app-level chart series colors; slide chart tokens below
  // still get a deck-safe palette for generated deck rendering. Semantic
  // status tokens like --chart-negative remain foundation-owned.
  if (appChartTuples) {
    setToken('--chart-primary', appChartTuples[0])
    setToken('--chart-secondary', appChartTuples[1])
    setToken('--chart-tertiary', appChartTuples[2])
    setToken('--chart-accent', appChartTuples[3])
    setToken('--chart-quinary', appChartTuples[4])
    setToken('--chart-senary', appChartTuples[5])
    setToken('--chart-bg-dark', deepTuple)
    setToken('--chart-bg-dark-rgb', `${deepRgb.r}, ${deepRgb.g}, ${deepRgb.b}`)
  }

  // ── Slide semantic tokens ──
  // Text, accent wash, panels, rules and chart slots 1–8 (red-safe accent and
  // wash below override their two roles).
  for (const [key, value] of Object.entries(deckThemeVars)) {
    setToken(key, value)
  }

  // --slide-accent: brand emphasis for bullets, KPIs, labels, and decorative emphasis.
  // Red brands fall back to black so emphasis never reads as error/danger.
  // --slide-highlight: alias of --slide-accent for legacy decorative emphasis.
  // --slide-accent-soft: light surface tint for card backgrounds; red brands use neutral grey.
  // Separate tokens preserve semantic meaning for future independent derivation paths.
  setToken('--slide-accent', slideBrandEmphasisCss)
  setToken('--slide-accent-foreground', tupleToCss(slideBrandEmphasisForegroundTuple))
  setToken('--slide-accent-alt', tupleToCss(roleTokens.brand.surfaceStrong))
  setToken('--slide-accent-alt-foreground', tupleToCss(getPrimaryForeground(roleTokens.brand.surfaceStrong)))
  setToken('--slide-accent-soft', slideAccentSoftCss)
  setToken('--slide-highlight', slideBrandEmphasisCss)
  setToken('--slide-highlight-soft', highlightSoftCss)

  setToken('--slide-success', tupleToCss(roleTokens.semantic.success))
  setToken('--slide-success-soft', tupleToCss(roleTokens.semantic.successSoft))
  setToken('--slide-warning', tupleToCss(roleTokens.semantic.warning))
  setToken('--slide-warning-soft', tupleToCss(roleTokens.semantic.warningSoft))
  setToken('--slide-danger', tupleToCss(roleTokens.semantic.danger))
  setToken('--slide-danger-soft', tupleToCss(roleTokens.semantic.dangerSoft))
  setToken('--slide-info', tupleToCss(roleTokens.semantic.info))
  setToken('--slide-info-soft', tupleToCss(roleTokens.semantic.infoSoft))

  // ── Slide diverging tokens ──
  // The negative pole is a hardcoded, non-brand red so its meaning never
  // shifts with theme or brand. When the brand chart color is itself close
  // enough to that red to collide (see isDivergingUnsafeAccent), the positive
  // pole falls back to a validated non-brand green instead of the brand hue.
  const divergingPositiveUnsafe = isDivergingUnsafeAccent(chartPrimaryTuple)
  setToken(
    '--slide-diverging-positive',
    divergingPositiveUnsafe ? tupleToCss(DIVERGING_SAFE_POSITIVE) : chartPrimaryCss,
  )
  setToken(
    '--slide-diverging-positive-soft',
    divergingPositiveUnsafe
      ? tupleToCss(DIVERGING_SAFE_POSITIVE_SOFT)
      : `color-mix(in srgb, ${chartPrimaryCss} 14%, var(--slide-bg, #ffffff))`,
  )
  setToken('--slide-diverging-negative', tupleToCss('0 72% 42%'))
  setToken('--slide-diverging-negative-soft', tupleToCss('0 72% 94%'))
  // True neutral for the diverging middle band: aliases the existing
  // brand-agnostic neutral scale (never written from accent/danger) rather
  // than inventing a new tinted gray.
  setToken('--slide-diverging-neutral', 'var(--slide-neutral-mid)')

  // ── Slide caution ──
  setToken('--slide-caution-bg', tupleToCss(roleTokens.semantic.warningSoft))
  setToken('--slide-caution-text', tupleToCss(roleTokens.semantic.warning))
  setToken('--slide-caution-text-strong', tupleToCss(roleTokens.semantic.warning))

  // ── Slide cover ──
  setToken('--slide-cover-bg', deepCss)
  setToken('--slide-cover-text', tupleToCss(deepForegroundTuple))
  setToken('--slide-cover-text-muted', `color-mix(in srgb, ${tupleToCss(deepForegroundTuple)} 78%, transparent)`)

  // ── Slide brand card/deep ──
  setToken('--slide-brand-card-accent', cardCss)
  setToken('--slide-brand-card-bg', brandCardBgCss)
  setToken('--slide-brand-card-border', brandCardBorderCss)
  setToken('--slide-brand-card-foreground', tupleToCss(cardForegroundTuple))
  setToken('--slide-brand-deep-bg', deepCss)
  setToken('--slide-brand-deep-foreground', tupleToCss(deepForegroundTuple))

  // ── Slide heatmap ──
  // Heatmap soft is derived from the primary data color and slide surface. It
  // must not depend on an arbitrary categorical palette slot.
  setToken(
    '--slide-heatmap-green-start',
    `color-mix(in srgb, ${chartPrimaryCss} 14%, var(--slide-bg, #ffffff))`,
  )
  setToken('--slide-heatmap-green-end', chartPrimaryCss)
  setToken('--slide-heatmap-green-star', chartPrimaryCss)
  setToken('--slide-heatmap-red-start', tupleToCss(roleTokens.semantic.dangerSoft))
  setToken('--slide-heatmap-red-end', tupleToCss(roleTokens.semantic.danger))
  setToken('--slide-heatmap-red-star', tupleToCss(roleTokens.semantic.danger))
  setToken('--slide-heatmap-red-text', deckTheme.textPrimary)
  setToken('--slide-heatmap-green-text', chartPrimaryForegroundCss)

  setToken('--slide-neutral-medium', 'var(--slide-neutral-mid)')
  setToken('--brand-hue-rotate', `${Math.round(accentParsed.h - 278)}deg`)

  // ── DS bridge tokens ──
  const accentCss = tupleToCss(accentTuple)
  const brandBorderCss = tupleToCss(roleTokens.brand.border)
  const homeCardActiveBorderCss =
    resolvedTheme === 'dark' && isRedAccentTuple(accentTuple)
      ? 'hsl(0 0% 100%)'
      : brandBorderCss
  const reportSecondaryIconUsesNeutralFallback = isRedAccentTuple(accentTuple)
  const reportSecondaryIconBgCss = reportSecondaryIconUsesNeutralFallback
    ? resolvedTheme === 'dark'
      ? 'color-mix(in srgb, hsl(var(--card)) 88%, hsl(var(--card-foreground)) 12%)'
      : 'color-mix(in srgb, hsl(var(--foreground)) 6%, transparent)'
    : accentCss
  setToken('--ds-accent', accentCss)
  setToken('--ds-accent-alt', cardCss)
  setToken('--ds-chart-accent', chartPrimaryCss)
  setToken('--ds-chart-accent-alt', chartMidCss)
  setToken('--ds-accent-secondary', chartPrimaryCss)
  setToken('--ds-quadrant-badge-border', chartPrimaryCss)
  setToken('--ds-red-tint', slideAccentSoftCss)
  setToken('--ds-red-tint-light', highlightSoftCss)
  setToken('--ds-danger-soft', slideAccentSoftCss)
  setToken('--ds-heatmap-high-bg', `color-mix(in srgb, ${chartPrimaryCss} 12%, transparent)`)
  setToken('--ds-heatmap-high-text', chartPrimaryCss)
  setToken('--ds-heatmap-mid-bg', `color-mix(in srgb, ${chartMidCss} 15%, transparent)`)
  setToken('--ds-heatmap-mid-text', chartMidCss)

  const coverAccentTuple = getCoverAccentTuple(accentParsed)
  const coverAccentCss = tupleToCss(coverAccentTuple)
  const coverAccentRgb = hslToRgb(coverAccentTuple)
  const coverAccentAlpha = getCoverAccentAlpha(coverAccentRgb)
  const coverGraphite = getCoverGraphiteTuples(accentParsed)
  const coverBrandFieldTuple = getCoverBrandFieldTuple(accentParsed)
  const coverBrandFieldRgb = hslToRgb(coverBrandFieldTuple)
  setToken('--ds-cover-accent', coverAccentCss)
  setToken('--ds-cover-accent-rgb', `${coverAccentRgb.r}, ${coverAccentRgb.g}, ${coverAccentRgb.b}`)
  setToken('--ds-cover-brand-field-rgb', `${coverBrandFieldRgb.r}, ${coverBrandFieldRgb.g}, ${coverBrandFieldRgb.b}`)
  setToken('--ds-cover-accent-alpha', coverAccentAlpha.toFixed(2))
  setToken('--ds-cover-brand-field-alpha', getCoverBrandFieldAlpha(coverBrandFieldRgb).toFixed(2))
  setToken('--ds-cover-pattern-opacity', clamp(coverAccentAlpha + 0.18, 0.42, 0.56).toFixed(2))
  setToken('--ds-cover-graphite-bg', tupleToCss(coverGraphite.base))
  setToken('--ds-cover-graphite-bg-deep', tupleToCss(coverGraphite.deep))
  setToken('--ds-cover-graphite-bg-soft', tupleToCss(coverGraphite.soft))
  // Cover metadata labels sit on the deep cover background; use the resolved
  // cover foreground instead of a fixed Fairgen mint so monochrome brands stay neutral.
  setToken('--ds-cover-meta-label-accent', `color-mix(in srgb, ${tupleToCss(deepForegroundTuple)} 82%, transparent)`)

  // ── Slide chart palette ── slots 1–8 come with the deck theme above.
  // Slots 9–11 are content/neutral tones (not brand palette entries).
  // They exist for edge-case charts needing >8 series — do not use as brand colors.
  setToken('--slide-chart-9', deckTheme.textPrimary)
  setToken('--slide-chart-10', deckTheme.textSecondary)
  setToken('--slide-chart-11', deckTheme.textMuted)

  // ── Sidebar tokens (conditional on theme) ──
  const pageBackgroundTuple = resolveThemeSurfaceTuple(
    settings,
    '--background',
    resolvedTheme,
    resolvedTheme === 'dark' ? '180 52% 6.5%' : '0 0% 100%',
  )
  const cardSurfaceTuple = resolveThemeSurfaceTuple(
    settings,
    '--card',
    resolvedTheme,
    resolvedTheme === 'dark' ? '167 37% 10%' : '0 0% 94.5%',
  )
  const readableBrandTextOnCard = (tuple: string) =>
    getReadableBrandTextOnSurface(tuple, cardSurfaceTuple, resolvedTheme)
  const actionFg = getReadableOnBrandSurfaceForeground(accentTuple, resolvedTheme)
  const ctaFg = getReadableOnBrandSurfaceForeground(ctaTuple, resolvedTheme)
  const ctaCss = tupleToCss(ctaTuple)
  const homeChipBg = 'color-mix(in srgb, hsl(var(--primary)) 50%, transparent)'
  const homeChipBgHover = 'color-mix(in srgb, hsl(var(--primary)) 60%, transparent)'
  const homeChipFg = resolvedTheme === 'dark'
    ? 'hsl(var(--card-foreground))'
    : 'hsl(var(--foreground))'

  setToken('--sidebar-primary', accentTuple)
  setToken('--sidebar-primary-foreground', actionFg)
  setToken('--sidebar-ring', accentTuple)

  // ── Brand action tokens (full scope only — caller gates white-label) ──
  setToken('--primary', accentTuple)
  setToken('--primary-foreground', actionFg)
  setToken(
    '--primary-text-on-background',
    getReadableBrandTextOnSurface(accentTuple, pageBackgroundTuple, resolvedTheme),
  )
  setToken('--primary-text-on-card', readableBrandTextOnCard(accentTuple))
  setToken('--report-action', readableBrandTextOnCard(accentTuple))
  setToken('--report-accent-light', readableBrandTextOnCard(accentTuple))
  setToken('--report-accent-mid', readableBrandTextOnCard(accentTuple))
  setToken('--ring', accentTuple)
  setToken('--cta', ctaTuple)
  setToken('--cta-foreground', ctaFg)
  setToken(
    '--sidebar-cta-gradient',
    `linear-gradient(135deg, ${ctaCss} 0%, color-mix(in srgb, ${ctaCss} 70%, white) 100%)`,
  )
  setToken('--sidebar-cta-foreground', tupleToCss(ctaFg))
  setToken('--share-action', ctaTuple)
  setToken('--share-action-foreground', ctaFg)
  setToken('--share-gate-icon', accentTuple)
  setToken('--share-gate-icon-background', accentTuple)
  setToken('--twin-chat-accent', accentTuple)
  const twinChatSend = resolveTwinChatSendTuple(accentTuple, cardTuple, resolvedTheme)
  setToken('--twin-chat-send', twinChatSend.send)
  setToken('--twin-chat-send-fg', twinChatSend.sendFg)
  setToken('--twin-chat-bubble', resolveTwinChatBubbleTuple(roleTokens, resolvedTheme))
  setToken('--home-card-active-border', homeCardActiveBorderCss)
  setToken('--home-card-active-check-bg', `color-mix(in srgb, ${accentCss} 28%, transparent)`)
  setToken('--home-card-active-check-fg', tupleToCss(actionFg))
  setToken(
    '--home-card-shadow',
    `0 2px 14px color-mix(in srgb, ${deepCss} 12%, transparent)`,
  )
  setToken(
    '--home-card-shadow-active',
    `0 18px 46px color-mix(in srgb, ${deepCss} 22%, transparent), 0 0 0 1px color-mix(in srgb, ${homeCardActiveBorderCss} 32%, transparent)`,
  )
  setToken('--report-secondary-icon-bg', reportSecondaryIconBgCss)
  setToken(
    '--report-secondary-icon-fg',
    reportSecondaryIconUsesNeutralFallback
      ? 'hsl(var(--muted-foreground))'
      : tupleToCss(actionFg),
  )
  // Marketplace audience badge: brand primary circle with matching foreground.
  setToken('--audience-badge-bg', accentTuple)
  setToken('--audience-badge-fg', tupleToCss(actionFg))
  // Identity-mix slider track: brand-neutral. Light keeps the foundation grey;
  // dark uses a neutral graphite (lighter than Fairgen's deep green) so the rail
  // doesn't read as Fairgen green on a non-green brand.
  setToken(
    '--audience-slider-track',
    resolvedTheme === 'dark' ? tupleToCss('0 0% 20%') : tupleToCss('0 0% 88.6%'),
  )
  // Segment option chips (dark only): mirror the sidebar selected color instead of
  // Fairgen's deep-teal --card-inset. Light mode is unaffected (chips use bg-primary/10).
  if (resolvedTheme === 'dark') {
    setToken('--segment-option-bg', 'var(--sidebar-nav-active-bg)')
  }
  setToken('--home-chip-bg', homeChipBg)
  setToken('--home-chip-bg-hover', homeChipBgHover)
  setToken('--home-chip-fg', homeChipFg)
  // White-label orgs: paint the home utility chip icon with the same color as
  // the chip label so the Shuffle arrow and text read as one unit. The Fairgen
  // foundation paints the arrow with --platform-card/--platform-deep, which we
  // intentionally do NOT propagate to branded orgs.
  setToken('--home-chip-icon', homeChipFg)
  // Progress bar accent — red brands fall back to foreground so filter bars
  // don't visually conflict with destructive/error semantics.
  setToken(
    '--progress-bar-accent',
    isRedAccentTuple(accentTuple) ? 'var(--foreground)' : accentTuple,
  )
  // Plan "Most popular" badge accent — branded for non-red orgs, but red brands
  // fall back to foreground so the highlight never reads as a destructive/error
  // color on the pricing cards.
  setToken(
    '--plan-badge-accent',
    isRedAccentTuple(accentTuple) ? 'var(--foreground)' : accentTuple,
  )

  const wizardStepTokens = resolveWizardStepTokens(
    accentTuple,
    ctaTuple,
    actionFg,
    cardTuple,
    cardForegroundTuple,
    pageBackgroundTuple,
    resolvedTheme,
  )
  for (const [key, value] of Object.entries(wizardStepTokens)) {
    setToken(key, value)
  }
  setToken(
    '--accent-highlight',
    resolvedTheme === 'dark'
      ? wizardStepTokens['--step-active-bg']
      : accentTuple,
  )

  // ── Onboarding brand chrome (full scope only) ──────────────────────────────
  // Foundation-only tokens (study-card-bg, study-card-description, preview-bg)
  // are intentionally not written here — they stay in globals.css for all orgs.

  // Study category pills: brand deep bg + foreground derived from brand deep.
  const pillFgTuple = getPrimaryForeground(deepTuple)
  setToken('--onboarding-study-pill-bg', deepTuple)
  setToken('--onboarding-study-pill-fg', pillFgTuple)
  setToken('--onboarding-study-pill-border', deepTuple)

  // Brand setup hero: deep surface + matching foreground.
  setToken('--onboarding-brand-hero-bg', deepTuple)
  setToken('--onboarding-brand-hero-fg', deepForegroundTuple)

  // Hero glow: brand accent radials (the same opacity recipe as foundation,
  // but using brand accent instead of Fairgen platform-card mint).
  const heroBrandGlowAccentCss = tupleToCss(accentTuple)
  setToken(
    '--onboarding-brand-hero-glow',
    `radial-gradient(at 50% 30%, color-mix(in srgb, ${heroBrandGlowAccentCss} 20%, transparent) 0px, transparent 50%),` +
    `radial-gradient(at 20% 70%, color-mix(in srgb, ${heroBrandGlowAccentCss} 10%, transparent) 0px, transparent 50%)`,
  )

  // Portrait stack deep stop: brand accent in dark-equivalent surface role.
  // Matches the corrective role of --onboarding-portrait-deep in globals.css.
  setToken('--onboarding-portrait-deep', accentTuple)

  return tokenValues
}

/**
 * Remove inline properties written by applyThemeBrandTokens from
 * document.documentElement.style, letting preset CSS / base :root values take over.
 *
 * Tokens that have an active restore stack (written by a live component using
 * `restore: true`) are intentionally skipped — they are owned by that component
 * and will be cleaned up by its disposer on unmount or dependency change.
 * Clearing them here would cause the owning component's UI to flicker to
 * foundation defaults until the next apply cycle.
 *
 * Exception: if a token has a restore stack but NO inline value (the stack was
 * orphaned), the stack is still deleted to prevent leaks.
 */
export function resetThemeBrandTokens(scope: ThemeBrandTokenScope = 'full') {
  const root = document.documentElement.style
  const keys = getThemeBrandResetTokenKeys(scope)
  for (const key of keys) {
    const stack = tokenStacks.get(key)
    if (stack && stack.length > 0) {
      // Token is owned by a live restore-tracked caller; skip clearing it.
      // The component's disposer will restore the correct previous value when
      // it unmounts or its dependency changes.
      continue
    }
    root.removeProperty(key)
    tokenStacks.delete(key)
  }
}
